import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { contact_name: "", email: "", phone: "", role: "", notes: "" };

export default function JobSeekerDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const inputCls =
    "bg-[#0F111A] border-white/10 text-white placeholder:text-slate-500 rounded-sm focus-visible:ring-sky-500";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        lead_type: "staff_applicant",
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        roles_needed: form.role ? [form.role] : [],
        notes: form.notes,
      });
      toast.success(
        "Application received! Our recruiting team will reach out about upcoming orientation dates."
      );
      setForm(initial);
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131622] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold uppercase">
            Join the <span className="text-sky-500">Crew</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Work the biggest concerts, games, and festivals in your city.
            Flexible shifts, weekly pay, full training provided.
          </DialogDescription>
        </DialogHeader>
        <form data-testid="job-seeker-form" onSubmit={submit} className="space-y-4 mt-2">
          <Input
            data-testid="job-form-input-name"
            placeholder="Full Name *"
            value={form.contact_name}
            onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
            className={inputCls}
          />
          <Input
            data-testid="job-form-input-email"
            type="email"
            placeholder="Email *"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
          />
          <Input
            data-testid="job-form-input-phone"
            placeholder="Phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={inputCls}
          />
          <Select
            value={form.role}
            onValueChange={(v) => setForm({ ...form, role: v })}
          >
            <SelectTrigger data-testid="job-form-select-role" className={inputCls}>
              <SelectValue placeholder="Role you're interested in" />
            </SelectTrigger>
            <SelectContent className="bg-[#131622] border-white/10 text-white">
              <SelectItem value="Ushers">Usher / Wayfinder</SelectItem>
              <SelectItem value="Ticket Scanners">Ticket Scanner</SelectItem>
              <SelectItem value="VIP Hospitality">VIP Hospitality</SelectItem>
              <SelectItem value="Concessions">Concessions / Bar</SelectItem>
              <SelectItem value="Crowd Safety">Crowd Safety</SelectItem>
              <SelectItem value="Stagehands">Stagehand / Teardown</SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            data-testid="job-form-input-notes"
            placeholder="Experience, availability, city..."
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className={`${inputCls} min-h-[90px]`}
          />
          <button
            data-testid="job-form-submit-button"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-[#090A0F] font-bold rounded-sm px-6 py-3 transition-colors duration-200"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Sending..." : "Submit Application"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
